import { useState, useEffect, useRef } from "react";
import { InvestorOverview, InvestorActivity, InvestorSettings } from "./InvestorProfilePage";
import { AnalystOverview, AnalystActivity, AnalystSettings } from "./AnalystProfilePage";
import { AdvisorOverview, AdvisorActivity, AdvisorSettings } from "./AdvisorProfilePage";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  Camera,
  Upload,
  X,
  CreditCard,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast as toastLib } from "sonner";
import { userService } from "../../services/userService";
import { uploadService } from "../../services/uploadService";
import { aiService } from "../../services/aiService";
import subscriptionService from "../../services/subscriptionService";
import { subscriptionPrices } from "../../config/subscriptionPrices";
import { advisorService } from "../../services/advisorService";
import { analystService } from "../../services/analystService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { RISK_TOLERANCE_LEVELS, INVESTMENT_EXPERIENCE_LEVELS, SECTORS, CERTIFICATIONS, RESEARCH_STYLES, FINANCIAL_SPECIALIZATIONS } from "../../utils/financialTaxonomy";
import { type Occupation, getFormattedOccupation, type OnboardingData, type Address, getFullName, getFormattedAddress } from "../../interface";
import type { FeeStructureOption, FeeType, BillingFrequency } from "../../interface/profile/FeeStructureOption";

type Role = "investor" | "analyst" | "advisor";

interface ProfilePageProps {
  role: Role;
  userId?: string;
}

interface ProfileFormData extends OnboardingData {
  bio: string;
  profilePictureUrl: string;
  feeStructureOptions: FeeStructureOption[];
  consultationAvailable: boolean;
}

const defaultProfile = (): ProfileFormData => ({
  role: "",
  fullName: "", email: "", firstName: "", lastName: "", phoneNumber: "",
  dateOfBirth: "", linkedinUrl: "",
  address: { addressLine1: "", addressLine2: "", city: "", state: "", zipCode: "", country: "" },
  occupation: { occupationType: "", company: "", jobTitle: "", details: "" },
  bio: "", profilePictureUrl: "",
  feeStructureOptions: [], consultationAvailable: false, acceptingNewClients: false, consultationDuration: 0,
  riskTolerance: "", investmentGoals: [], preferredResearchStyles: [],
  portfolioSize: "", investmentExperience: "", sectors: [],
  researchExpertise: [], investmentFocus: [], yearsExperience: "",
  focusSectors: [], certifications: [], clientRiskTolerance: [],
  advisorCertifications: [], specializations: [], yearsAdvising: "", clientTypes: [],
});

export function ProfilePage({ role, userId }: ProfilePageProps) {
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [displayPictureUrl, setDisplayPictureUrl] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileFormData>(defaultProfile());
  const [initialProfile, setInitialProfile] = useState<ProfileFormData>(defaultProfile());
  const [memberSince, setMemberSince] = useState("");
  const [advisorAum, setAdvisorAum] = useState<number | null>(null);
  const [advisorTotalClients, setAdvisorTotalClients] = useState<number | null>(null);
  const [analystTotalClients, setAnalystTotalClients] = useState<number | null>(null);
  const [analystAvgReturn, setAnalystAvgReturn] = useState<number | null>(null);

  const [subStatus, setSubStatus] = useState<any>(null);
  const [subLoading, setSubLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancelInput, setShowCancelInput] = useState(false);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    setSubLoading(true);
    try {
      const res = await subscriptionService.getStatus();
      setSubStatus(res.data || res);
    } catch {
      setSubStatus(null);
    } finally {
      setSubLoading(false);
    }
  };

  const handleSubscribe = async () => {
    const plan = localStorage.getItem('plan') === 'ENTERPRISE' ? 'ENTERPRISE' :
                 localStorage.getItem('userRole') === 'ANALYST' ? 'ANALYST' : 'ADVISOR';
    try {
      const res = await subscriptionService.createCheckout(plan);
      const data = res.data || res;
      if (data.url) window.location.href = data.url;
    } catch {
      toastLib.error("Failed to start checkout");
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await subscriptionService.cancel(cancelReason || undefined);
      toastLib.success("Subscription cancelled");
      setShowCancelInput(false);
      setCancelReason("");
      loadSubscriptionStatus();
    } catch (e: any) {
      toastLib.error(e?.message || "Failed to cancel subscription");
    }
  };

  useEffect(() => {
    if (!profilePicture) {
      setDisplayPictureUrl(null);
      return;
    }
    aiService.generateS3PresignedUrl(profilePicture)
      .then(res => {
        const data = res?.data || res;
        setDisplayPictureUrl(data.presignedUrl || data.url || data);
      })
      .catch(() => setDisplayPictureUrl(profilePicture));
  }, [profilePicture]);

  useEffect(() => {
    loadUserProfile();
  }, [userId, role]);

  const loadUserProfile = async () => {
    try {
      const res = await userService.getProfile(userId);
      const d = res?.data || res;
      if (!d) return;
      const addr = d.address || {};
      const occ = d.occupation || {};
      const p: ProfileFormData = {
        role: d.role || "",
        fullName: d.fullName || "", firstName: d.firstName || "", lastName: d.lastName || "",
        email: d.email || "", phoneNumber: d.phoneNumber || "",
        dateOfBirth: d.dateOfBirth || "",
        linkedinUrl: d.linkedinUrl || "",
        address: {
          addressLine1: addr.addressLine1 || "",
          addressLine2: addr.addressLine2 || "",
          city: addr.city || "",
          state: addr.state || "",
          zipCode: addr.zipCode || "",
          country: addr.country || "",
        },
        occupation: {
          occupationType: occ.occupationType || "",
          company: occ.company || "",
          jobTitle: occ.jobTitle || "",
          details: occ.details || occ.occupationDetails || "",
        },
        bio: d.bio || "",
        profilePictureUrl: d.profilePictureUrl || "",
        feeStructureOptions: [], consultationAvailable: false, acceptingNewClients: false, consultationDuration: 0,
        riskTolerance: "", investmentGoals: [], preferredResearchStyles: [],
        portfolioSize: "", investmentExperience: "", sectors: [],
        researchExpertise: [], investmentFocus: [], yearsExperience: "",
        focusSectors: [], certifications: [], clientRiskTolerance: [],
        advisorCertifications: [], specializations: [], clientTypes: [],
        yearsAdvising: ""
      };
      const r = role?.toLowerCase();
      const rp = r === "investor" ? d.investorProfile : r === "advisor" ? d.advisorProfile : d.analystProfile;
      if (rp) {
        p.riskTolerance = rp.riskTolerance || "";
        p.investmentGoals = rp.investmentGoal || rp.investmentGoals || [];
        p.preferredResearchStyles = rp.preferredResearchStyles || rp.preferredResearchStyle || [];
        p.portfolioSize = rp.portfolioSizeRange || "";
        p.investmentExperience = rp.investmentExperience || rp.yearsInvesting || "";
        p.sectors = rp.preferredSectors || rp.focusSectors || [];
        p.researchExpertise = rp.researchExpertise || [];
        p.investmentFocus = rp.investmentFocus || [];
        p.yearsExperience = rp.yearsExperience || "";
        p.focusSectors = rp.focusSectors || rp.preferredSectors || [];
        p.certifications = rp.certifications || rp.advisorCertifications || [];
        p.clientRiskTolerance = rp.clientRiskTolerance || [];
        p.researchApproach = rp.researchApproach || "";
        p.advisorCertifications = rp.advisorCertifications || rp.certifications || [];
        p.advisoryApproach = rp.advisoryApproach || "";
        p.specializations = rp.specialization || rp.specializations || [];
        p.yearsAdvising = rp.yearsExperience || rp.yearsAdvising || "";
        p.clientTypes = rp.clientTypes || [];
        p.feeStructureOptions = rp.feeStructureOptions || [];
        p.acceptingNewClients = rp.acceptingNewClients ?? false;
        p.consultationAvailable = rp.consultationAvailable ?? false;
        p.consultationDuration = rp.consultationDuration ?? 0;
        p.bio = rp.bio || p.bio;
        p.linkedinUrl = rp.linkedinUrl || p.linkedinUrl;
      }
      setProfile(p);
      setInitialProfile({ ...p });
      setMemberSince(d.createdAt ? new Date(d.createdAt).toLocaleString("default", { month: "long", year: "numeric" }) : "");
      if (r === "advisor" && userId) {
        advisorService.getClientSummary(userId).then(res => {
          const summary = res?.data || res;
          if (summary) {
            setAdvisorAum(summary.totalAum ?? null);
            setAdvisorTotalClients(summary.totalClients ?? null);
          }
        }).catch(() => {});
      }
      if (r === "analyst" && userId) {
        analystService.getClients(userId).then(res => {
          const clients = res?.data || res || [];
          const list = Array.isArray(clients) ? clients : [];
          setAnalystTotalClients(list.length);
          setAnalystAvgReturn(list.length > 0 ? list.reduce((sum: number, c: any) => sum + (c.totalPercentageReturn || 0), 0) / list.length : null);
        }).catch(() => {});
      }
      if (d.profilePictureUrl) setProfilePicture(d.profilePictureUrl);
    } catch {
      // fallback to empty
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validation = userService.validateImageFile(file);
    if (!validation.valid) {
      toastLib.error(validation.message || validation.error || "Invalid file");
      return;
    }
    setSelectedFile(file);
    try {
      const base64 = await userService.fileToBase64(file);
      setTempImage(base64);
    } catch {
      toastLib.error("Failed to preview image");
    }
  };

  const handleSaveProfilePicture = async () => {
    if (!tempImage || !selectedFile) return;
    setIsUploading(true);
    try {
      const { url } = await uploadService.uploadFile(selectedFile, "profile");
      await userService.updateProfilePicture(userId,url);
      setProfilePicture(url);
      setUploadDialogOpen(false);
      setTempImage(null);
      setSelectedFile(null);
      toastLib.success("Profile picture updated successfully!");
    } catch (error) {
      toastLib.error(error instanceof Error ? error.message : "Failed to upload profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setUploadDialogOpen(false);
    setTempImage(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveProfilePicture = async () => {
    setIsDeleting(true);
    try {
      await userService.deleteProfilePicture(userId);
      setProfilePicture(null); setTempImage(null); setSelectedFile(null); setUploadDialogOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toastLib.success("Profile picture removed successfully!");
    } catch (error) {
      toastLib.error(error instanceof Error ? error.message : "Failed to remove profile picture");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setProfile({ ...initialProfile });
    setProfilePicture(initialProfile.profilePictureUrl || null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload: Record<string, any> = {
        fullName: profile.firstName + " " + profile.lastName,
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        phoneNumber: profile.phoneNumber,
        dateOfBirth: profile.dateOfBirth,
        occupation: profile.occupation,
        address: profile.address,
        bio: profile.bio,
        linkedinUrl: profile.linkedinUrl,
        profilePictureUrl: profilePicture || profile.profilePictureUrl,
      };

      const r = role?.toLowerCase();
      if (r === "investor") {
        const expLevel = INVESTMENT_EXPERIENCE_LEVELS.find(e => e.key === profile.investmentExperience.toLowerCase());
        payload.investorProfile = {
          riskTolerance: profile.riskTolerance,
          investmentGoal: profile.investmentGoals,
          preferredResearchStyle: profile.preferredResearchStyles,
          portfolioSizeRange: profile.portfolioSize,
          investmentExperience: profile.investmentExperience,
          yearsInvesting: expLevel?.value || profile.investmentExperience,
          preferredSectors: profile.sectors,
        };
      } else if (r === "analyst") {
        payload.analystProfile = {
          yearsExperience: profile.yearsExperience,
          certifications: profile.certifications,
          researchExpertise: profile.researchExpertise,
          focusSectors: profile.focusSectors,
          investmentFocus: profile.investmentFocus,
          researchApproach: profile.researchApproach,
          clientRiskTolerance: profile.clientRiskTolerance,
          feeStructureOptions: profile.feeStructureOptions,
          acceptingNewClients: profile.acceptingNewClients,
          consultationAvailable: profile.consultationAvailable,
          consultationDuration: profile.consultationDuration,
        };
      } else if (r === "advisor") {
        payload.advisorProfile = {
          yearsExperience: profile.yearsExperience,
          advisorCertifications: profile.advisorCertifications,
          specializations: profile.specializations,
          clientTypes: profile.clientTypes,
          clientRiskTolerance: profile.clientRiskTolerance,
          advisoryApproach: profile.advisoryApproach,
          feeStructureOptions: profile.feeStructureOptions,
          acceptingNewClients: profile.acceptingNewClients,
          consultationAvailable: profile.consultationAvailable,
          consultationDuration: profile.consultationDuration,
        };
      }

      await userService.updateProfile(userId, payload);
      toastLib.success("Profile updated successfully!");
      await loadUserProfile();
      setIsEditing(false);
    } catch (error) {
      toastLib.error(error instanceof Error ? error.message : "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const update = <K extends keyof ProfileFormData>(field: K, value: ProfileFormData[K]) =>
    setProfile(prev => ({ ...prev, [field]: value }));

  const toggleArray = (field: keyof ProfileFormData, item: string) => {
    const arr = (profile[field] as string[] | undefined) || [];
    update(field, arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item] as any);
  };

  const FeeStructureSection = () => {
    const [showForm, setShowForm] = useState(false);
    const [newFee, setNewFee] = useState<Partial<FeeStructureOption>>({ feeType: undefined, name: "" });

    const addFee = () => {
      if (!newFee.name || !newFee.feeType) return;
      const fee: FeeStructureOption = {
        id: `fee-${Date.now()}`,
        feeType: newFee.feeType as FeeType,
        name: newFee.name,
        hourlyRate: newFee.hourlyRate,
        flatFeeAmount: newFee.flatFeeAmount,
        aumPercentage: newFee.aumPercentage,
        performanceFeePercentage: newFee.performanceFeePercentage,
        minimumFee: newFee.minimumFee,
        billingFrequency: newFee.billingFrequency as BillingFrequency,
        description: newFee.description,
      };
      setProfile(prev => ({ ...prev, feeStructureOptions: [...prev.feeStructureOptions, fee] }));
      setNewFee({ feeType: undefined, name: "" });
      setShowForm(false);
    };

    const removeFee = (id: string) => {
      setProfile(prev => ({ ...prev, feeStructureOptions: prev.feeStructureOptions.filter(f => f.id !== id) }));
    };

    const fees = profile.feeStructureOptions || [];

    return (
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-medium">Fee Structure and Pricing</h3>

        {fees.length > 0 && (
          <div className="space-y-2">
            {fees.map(fee => (
              <div key={fee.id} className="p-3 border rounded-lg flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{fee.name}</span>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{fee.feeType?.replace(/_/g, " ")}</span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    {fee.aumPercentage != null && <div>{fee.aumPercentage > 100 ? `AUM/Retainer: $${fee.aumPercentage}` : `AUM: ${fee.aumPercentage}%`}</div>}
                    {fee.hourlyRate != null && <div>Hourly: ${fee.hourlyRate}/hr</div>}
                    {fee.flatFeeAmount != null && <div>Flat Fee: ${fee.flatFeeAmount}</div>}
                    {fee.performanceFeePercentage != null && <div>Performance Fee: ${fee.performanceFeePercentage}%</div>}
                    {fee.commissionRate != null && <div>Commission: {fee.commissionRate}%</div>}
                    {fee.projectFeeAmount != null && <div>Project Fee: ${fee.projectFeeAmount}</div>}
                    {fee.subscriptionAmount != null && <div>Subscription: ${fee.subscriptionAmount}</div>}
                    {fee.retainerAmount != null && <div>Retainer: ${fee.retainerAmount}</div>}
                    {fee.perRequestAmount != null && <div>Per Request: ${fee.perRequestAmount}</div>}
                    {fee.perReportAmount != null && <div>Per Report: ${fee.perReportAmount}</div>}
                    {fee.dataFeedAmount != null && <div>Data Feed: ${fee.dataFeedAmount}</div>}
                    {fee.apiAccessAmount != null && <div>API Access: ${fee.apiAccessAmount}</div>}
                    {fee.minimumFee != null && <div>Minimum: ${fee.minimumFee}</div>}
                    {fee.billingFrequency && <div>Billing: {fee.billingFrequency}</div>}
                    {fee.description && <div className="italic mt-1">{fee.description}</div>}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeFee(fee.id)}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}

        {showForm ? (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
            <div className="flex items-center justify-between">
              <Label className="text-base">Add Custom Fee Structure</Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setShowForm(false); setNewFee({ feeType: undefined, name: "" }); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feeName">Service Name *</Label>
              <Input id="feeName" placeholder={r === "advisor" ? "e.g., Comprehensive Wealth Management" : "e.g., Sector Deep-Dive Analysis"} value={newFee.name || ""} onChange={e => setNewFee(p => ({ ...p, name: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feeType">Pricing Model *</Label>
              <Select value={newFee.feeType} onValueChange={v => setNewFee(p => ({ ...p, feeType: v as FeeType }))}>
                <SelectTrigger id="feeType"><SelectValue placeholder="Select pricing model" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUM_BASED">AUM-Based (% of Assets)</SelectItem>
                  <SelectItem value="PERFORMANCE_BASED">Performance-Based</SelectItem>
                  <SelectItem value="COMMISSION">Commission-Based</SelectItem>
                  <SelectItem value="FLAT_FEE">Flat Fee</SelectItem>
                  <SelectItem value="HOURLY">Hourly Rate</SelectItem>
                  <SelectItem value="HYBRID">Hybrid Model</SelectItem>
                  <SelectItem value="PROJECT_BASED">Project-Based Fee</SelectItem>
                  <SelectItem value="SUBSCRIPTION">Subscription/Recurring</SelectItem>
                  <SelectItem value="RETAINER">Retainer/Advisory</SelectItem>
                  <SelectItem value="FREEMIUM">Freemium Model</SelectItem>
                  <SelectItem value="PER_REQUEST">Per Request</SelectItem>
                  <SelectItem value="PER_REPORT">Per Report</SelectItem>
                  <SelectItem value="DATA_FEED">Data Feed Access</SelectItem>
                  <SelectItem value="API_ACCESS">API Access</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newFee.feeType && (
              <div className="grid grid-cols-2 gap-4">
                {(newFee.feeType === "HOURLY" || newFee.feeType === "HYBRID") && (
                  <div className="space-y-2">
                    <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                    <Input id="hourlyRate" type="number" placeholder={r === "advisor" ? "e.g., 250" : "e.g., 150"} value={newFee.hourlyRate || ""} onChange={e => setNewFee(p => ({ ...p, hourlyRate: parseFloat(e.target.value) || undefined }))} />
                  </div>
                )}

                {(newFee.feeType === "FLAT_FEE" || newFee.feeType === "HYBRID") && (
                  <div className="space-y-2">
                    <Label htmlFor="flatFeeAmount">{r === "advisor" ? "Flat Fee ($)" : "Project/Report Fee ($)"}</Label>
                    <Input id="flatFeeAmount" type="number" placeholder={r === "advisor" ? "e.g., 5000" : "e.g., 2500"} value={newFee.flatFeeAmount || ""} onChange={e => setNewFee(p => ({ ...p, flatFeeAmount: parseFloat(e.target.value) || undefined }))} />
                  </div>
                )}

                {(newFee.feeType === "AUM_BASED" || newFee.feeType === "HYBRID") && (
                  <div className="space-y-2">
                    <Label htmlFor="aumPercentage">{r === "advisor" ? "AUM Percentage (%)" : "Monthly Retainer ($)"}</Label>
                    <Input id="aumPercentage" type="number" step={r === "advisor" ? "0.1" : "1"} placeholder={r === "advisor" ? "e.g., 1.0" : "e.g., 3000"} value={newFee.aumPercentage || ""} onChange={e => setNewFee(p => ({ ...p, aumPercentage: parseFloat(e.target.value) || undefined }))} />
                  </div>
                )}

                {(newFee.feeType === "PERFORMANCE_BASED" || newFee.feeType === "HYBRID") && (
                  <div className="space-y-2">
                    <Label htmlFor="performanceFee">Performance Fee (%)</Label>
                    <Input id="performanceFee" type="number" step="1" placeholder="e.g., 20" value={newFee.performanceFeePercentage || ""} onChange={e => setNewFee(p => ({ ...p, performanceFeePercentage: parseFloat(e.target.value) || undefined }))} />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="minimumFee">Minimum Fee ($) (Optional)</Label>
                  <Input id="minimumFee" type="number" placeholder={r === "advisor" ? "e.g., 1000" : "e.g., 500"} value={newFee.minimumFee || ""} onChange={e => setNewFee(p => ({ ...p, minimumFee: parseFloat(e.target.value) || undefined }))} />
                </div>
              </div>
            )}

            {r === "advisor" && newFee.feeType && (
              <div className="space-y-2">
                <Label>Billing Frequency (Optional)</Label>
                <div className="flex gap-2">
                  {["MONTHLY", "QUARTERLY", "ANNUALLY"].map(freq => (
                    <Button key={freq} type="button" variant={newFee.billingFrequency === freq ? "default" : "outline"} onClick={() => setNewFee(p => ({ ...p, billingFrequency: freq as BillingFrequency }))} className="flex-1">
                      {freq}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea id="description" placeholder={r === "advisor" ? "Describe what's included in this fee structure..." : "e.g., Includes 15-page report with industry comparison, financial modeling, and 1-hour consultation"} value={newFee.description || ""} onChange={e => setNewFee(p => ({ ...p, description: e.target.value }))} rows={3} />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" onClick={addFee} disabled={!newFee.feeType || !newFee.name} className="flex-1">Add Fee Structure</Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setNewFee({ feeType: undefined, name: "" }); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>+ Add Fee Structure</Button>
        )}
      </div>
    );
  };

  const avatarFallback = profile.firstName && profile.lastName
    ? `${profile.firstName[0]}${profile.lastName[0]}`
    : profile.fullName?.split(" ").map((n: string) => n[0]).join("") || "?";

  const r = role?.toLowerCase();
  const titles: Record<string, string> = { investor: "Individual Investor", analyst: "Senior Financial Analyst", advisor: "Certified Financial Advisor" };
  const roleTitle = titles[r] || "";

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="relative group">
              <Avatar className="h-24 w-24 cursor-pointer transition-opacity hover:opacity-80" onClick={() => setUploadDialogOpen(true)}>
                {displayPictureUrl && <AvatarImage src={displayPictureUrl} alt={profile.fullName} />}
                <AvatarFallback className="text-2xl">{avatarFallback}</AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={() => setUploadDialogOpen(true)}>
                <Camera className="h-8 w-8 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                <div>
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label htmlFor="efn">First Name</Label>
                        <Input id="efn" value={profile.firstName} onChange={e => update("firstName", e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="eln">Last Name</Label>
                        <Input id="eln" value={profile.lastName} onChange={e => update("lastName", e.target.value)} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl mb-1">{profile.fullName || `${profile.firstName} ${profile.lastName}`.trim()}</h2>
                      <p className="text-muted-foreground">{profile.occupation?.jobTitle || roleTitle}</p>
                    </>
                  )}
                </div>
                <div className="flex gap-2 mt-3 md:mt-0">
                  {isEditing ? (
                    <>
                      <Button variant="outline" onClick={handleCancel}>Cancel</Button>
                      <Button onClick={handleSave} disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
                    </>
                  ) : (
                    <Button className="gap-2" onClick={() => setIsEditing(true)}>
                      <Edit className="h-4 w-4" /> Edit Profile
                    </Button>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-4 [&_label]:font-semibold">
                  <div className="space-y-2">
                    <Label htmlFor="ee">Email Address</Label>
                    <Input id="ee" type="email" placeholder="john.doe@example.com" value={profile.email} disabled />
                    <p className="text-xs text-muted-foreground">Email cannot be changed. Contact admin to update your email address.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ep">Phone Number</Label>
                    <Input id="ep" type="tel" placeholder="+1 (555) 123-4567" value={profile.phoneNumber || ""} onChange={e => update("phoneNumber", e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edob">Date of Birth</Label>
                    <Input
                      id="edob"
                      type="date"
                      value={profile.dateOfBirth instanceof Date ? profile.dateOfBirth.toISOString().split("T")[0] : profile.dateOfBirth || ""}
                      onChange={e => update("dateOfBirth", e.target.value)}
                    />
                  </div>

                  {/* Occupation Section */}
                  <div className="space-y-4 pt-2">
                    <h4 className="font-medium">Occupation</h4>

                    <div className="space-y-2">
                      <Label htmlFor="eocc">Occupation Type</Label>
                      <Select value={profile.occupation?.occupationType} onValueChange={v => setProfile(prev => ({ ...prev, occupation: { ...prev.occupation, occupationType: v } }))}>
                        <SelectTrigger id="eocc"><SelectValue placeholder="Select occupation type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self-employed">Self Employed</SelectItem>
                          <SelectItem value="employed">Employed</SelectItem>
                          <SelectItem value="business-owner">Business Owner</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="retired">Retired</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(profile.occupation?.occupationType === "employed" || profile.occupation?.occupationType === "business-owner") && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="eco">Company</Label>
                          <Input id="eco" placeholder="Acme Corp" value={profile.occupation?.company || ""} onChange={e => setProfile(prev => ({ ...prev, occupation: { ...prev.occupation, company: e.target.value } }))} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ejt">Job Title</Label>
                          <Input id="ejt" placeholder="Investment Manager" value={profile.occupation?.jobTitle || ""} onChange={e => setProfile(prev => ({ ...prev, occupation: { ...prev.occupation, jobTitle: e.target.value } }))} />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="edet">Details</Label>
                      <Textarea id="edet" placeholder="Additional information about your occupation..." value={profile.occupation?.details || ""} onChange={e => setProfile(prev => ({ ...prev, occupation: { ...prev.occupation, details: e.target.value } }))} rows={3} />
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="space-y-4 pt-2">
                    <h4 className="font-medium">Address</h4>

                    <div className="space-y-2">
                      <Label htmlFor="ea1">Address Line 1</Label>
                      <Input id="ea1" placeholder="123 Main Street" value={profile.address?.addressLine1 || ""} onChange={e => setProfile(prev => ({ ...prev, address: { ...prev.address, addressLine1: e.target.value } }))} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ea2">Address Line 2</Label>
                      <Input id="ea2" placeholder="Apt 4B" value={profile.address?.addressLine2 || ""} onChange={e => setProfile(prev => ({ ...prev, address: { ...prev.address, addressLine2: e.target.value } }))} />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="eci">City</Label>
                        <Input id="eci" placeholder="New York" value={profile.address?.city || ""} onChange={e => setProfile(prev => ({ ...prev, address: { ...prev.address, city: e.target.value } }))} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="est">State</Label>
                        <Input id="est" placeholder="NY" value={profile.address?.state || ""} onChange={e => setProfile(prev => ({ ...prev, address: { ...prev.address, state: e.target.value } }))} />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="ezi">Zip Code</Label>
                        <Input id="ezi" placeholder="10001" value={profile.address?.zipCode || ""} onChange={e => setProfile(prev => ({ ...prev, address: { ...prev.address, zipCode: e.target.value } }))} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eco2">Country</Label>
                        <Input id="eco2" placeholder="United States" value={profile.address?.country || ""} onChange={e => setProfile(prev => ({ ...prev, address: { ...prev.address, country: e.target.value } }))} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="elu">LinkedIn URL</Label>
                    <Input id="elu" type="url" placeholder="https://www.linkedin.com/in/johndoe" value={profile.linkedinUrl || ""} onChange={e => update("linkedinUrl", e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ebio">Bio</Label>
                    <Textarea id="ebio" value={profile.bio} onChange={e => update("bio", e.target.value)} rows={3} />
                  </div>

                  {/* Role-specific onboarding selections */}
                  {r === "investor" && (
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-sm font-medium">Investment Preferences</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>Risk Tolerance</Label>
                          <Select value={profile.riskTolerance} onValueChange={v => update("riskTolerance", v)}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {RISK_TOLERANCE_LEVELS.map(rt => <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label>Portfolio Size</Label>
                          <Select value={profile.portfolioSize} onValueChange={v => update("portfolioSize", v)}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="under-50k">Under $50,000</SelectItem>
                              <SelectItem value="50k-250k">$50,000 - $250,000</SelectItem>
                              <SelectItem value="250k-1m">$250,000 - $1,000,000</SelectItem>
                              <SelectItem value="1m-5m">$1,000,000 - $5,000,000</SelectItem>
                              <SelectItem value="over-5m">Over $5,000,000</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label>Investment Experience</Label>
                          <Select value={profile.investmentExperience.toLowerCase()} onValueChange={v => update("investmentExperience", v)}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {INVESTMENT_EXPERIENCE_LEVELS.map(ie => <SelectItem key={ie.key} value={ie.key}>{ie.details}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Investment Goals</Label>
                        <div className="flex flex-wrap gap-2">
                          {FINANCIAL_SPECIALIZATIONS.map(g => (
                            <Button key={g} variant={profile.investmentGoals?.includes(g) ? "default" : "outline"} size="sm" onClick={() => toggleArray("investmentGoals", g)}>
                              {g}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Research Style (Select all that apply)</Label>
                        <div className="flex flex-wrap gap-2">
                          {RESEARCH_STYLES.map(style => (
                            <Button key={style.value} variant={profile.preferredResearchStyles?.includes(style.value) ? "default" : "outline"} size="sm" onClick={() => toggleArray("preferredResearchStyles", style.value)}>
                              {style.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Sectors</Label>
                        <div className="flex flex-wrap gap-2">
                          {SECTORS.map(s => (
                            <Button key={s} variant={profile.sectors?.includes(s) ? "default" : "outline"} size="sm" onClick={() => toggleArray("sectors", s)}>
                              {s}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {r === "analyst" && (
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-sm font-medium">Analyst Profile</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>Years of Experience</Label>
                          <Select value={profile.yearsExperience} onValueChange={v => update("yearsExperience", v)}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0-2">0-2 years</SelectItem>
                              <SelectItem value="3-5">3-5 years</SelectItem>
                              <SelectItem value="6-10">6-10 years</SelectItem>
                              <SelectItem value="10+">10+ years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Certifications</Label>
                        <div className="flex flex-wrap gap-2">
                          {CERTIFICATIONS.map(c => (
                            <Button key={c.value} variant={profile.certifications?.includes(c.value) ? "default" : "outline"} size="sm" onClick={() => toggleArray("certifications", c.value)}>
                              {c.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Research Expertise</Label>
                        <div className="flex flex-wrap gap-2">
                          {RESEARCH_STYLES.map(rs => (
                            <Button key={rs.value} variant={profile.researchExpertise?.includes(rs.value) ? "default" : "outline"} size="sm" onClick={() => toggleArray("researchExpertise", rs.value)}>
                              {rs.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Investment Focus (Select all that apply)</Label>
                        <div className="flex flex-wrap gap-2">
                          {FINANCIAL_SPECIALIZATIONS.map(f => (
                            <Button key={f} variant={profile.investmentFocus?.includes(f) ? "default" : "outline"} size="sm" onClick={() => toggleArray("investmentFocus", f)}>
                              {f}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Focus Sectors</Label>
                        <div className="flex flex-wrap gap-2">
                          {SECTORS.map(s => (
                            <Button key={s} variant={profile.focusSectors?.includes(s) ? "default" : "outline"} size="sm" onClick={() => toggleArray("focusSectors", s)}>
                              {s}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="researchApproach">Research Approach (Optional)</Label>
                        <Textarea id="researchApproach" placeholder="Describe your research methodology..." value={profile.researchApproach || ""} onChange={e => update("researchApproach", e.target.value)} rows={3} />
                      </div>

                      <div className="space-y-2">
                        <Label>Preferred Client Risk Tolerance (Select all that apply)</Label>
                        <p className="text-xs text-muted-foreground">What risk profiles do you prefer to work with?</p>
                        <div className="flex flex-wrap gap-2">
                          {RISK_TOLERANCE_LEVELS.map(level => (
                            <Button key={level.value} variant={profile.clientRiskTolerance?.includes(level.value) ? "default" : "outline"} size="sm" onClick={() => toggleArray("clientRiskTolerance", level.value)}>
                              {level.label}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Fee Structure & Pricing */}
                      <FeeStructureSection />

                      {/* Additional Settings */}
                      <div className="space-y-4 pt-4 border-t">
                        <h3 className="text-sm font-medium">Additional Settings</h3>
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>Offer Free Initial Consultation</Label>
                            <p className="text-xs text-muted-foreground">Offer a free discovery call to discuss project scope and fit</p>
                          </div>
                          <Switch checked={profile.consultationAvailable || false} onCheckedChange={checked => update("consultationAvailable", checked)} />
                        </div>
                        {profile.consultationAvailable && (
                          <div className="space-y-2">
                            <Label htmlFor="consultationDuration">Consultation Duration (minutes)</Label>
                            <Input id="consultationDuration" type="number" placeholder="e.g., 30" value={profile.consultationDuration || ""} onChange={e => update("consultationDuration", parseInt(e.target.value) || 0)} />
                          </div>
                        )}
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div className="space-y-0.5">
                            <Label>Currently Accepting New Clients</Label>
                            <p className="text-xs text-muted-foreground">Allow potential clients to view your services and send project inquiries</p>
                          </div>
                          <Switch checked={profile.acceptingNewClients || false} onCheckedChange={checked => update("acceptingNewClients", checked)} />
                        </div>
                      </div>
                    </div>
                  )}

                  {r === "advisor" && (
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-sm font-medium">Advisor Profile</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>Years in Advisory</Label>
                          <Select value={profile.yearsExperience} onValueChange={v => update("yearsExperience", v)}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0-2">0-2 years</SelectItem>
                              <SelectItem value="3-5">3-5 years</SelectItem>
                              <SelectItem value="6-10">6-10 years</SelectItem>
                              <SelectItem value="10-15">10-15 years</SelectItem>
                              <SelectItem value="15+">15+ years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Certifications</Label>
                        <div className="flex flex-wrap gap-2">
                          {CERTIFICATIONS.map(c => (
                            <Button key={c.value} variant={profile.advisorCertifications?.includes(c.value) ? "default" : "outline"} size="sm" onClick={() => toggleArray("advisorCertifications", c.value)}>
                              {c.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Specializations</Label>
                        <div className="flex flex-wrap gap-2">
                          {FINANCIAL_SPECIALIZATIONS.map(s => (
                            <Button key={s} variant={profile.specializations?.includes(s) ? "default" : "outline"} size="sm" onClick={() => toggleArray("specializations", s)}>
                              {s}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Client Types</Label>
                        <div className="flex flex-wrap gap-2">
                          {["High Net Worth Individuals", "Mass Affluent", "Retirees", "Young Professionals", "Business Owners", "Families"].map(ct => (
                            <Button key={ct} variant={profile.clientTypes?.includes(ct) ? "default" : "outline"} size="sm" onClick={() => toggleArray("clientTypes", ct)}>
                              {ct}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Client Risk Tolerance</Label>
                        <div className="flex flex-wrap gap-2">
                          {RISK_TOLERANCE_LEVELS.map(rt => (
                            <Button key={rt.value} variant={profile.clientRiskTolerance?.includes(rt.value) ? "default" : "outline"} size="sm" onClick={() => toggleArray("clientRiskTolerance", rt.value)}>
                              {rt.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Advisory Philosophy (Optional)</Label>
                        <Textarea placeholder="Describe your advisory approach and philosophy..." value={profile.advisoryApproach || ""} onChange={e => update("advisoryApproach", e.target.value)} rows={3} />
                      </div>

                      {/* Fee Structure & Pricing */}
                      <FeeStructureSection />

                      {/* Additional Settings */}
                      <div className="space-y-4 pt-4 border-t">
                        <h3 className="text-sm font-medium">Additional Settings</h3>
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>Offer Free Initial Consultation</Label>
                            <p className="text-xs text-muted-foreground">Attract clients with a complimentary first meeting</p>
                          </div>
                          <Switch checked={profile.consultationAvailable || false} onCheckedChange={checked => update("consultationAvailable", checked)} />
                        </div>
                        {profile.consultationAvailable && (
                          <div className="space-y-2">
                            <Label htmlFor="consultationDuration">Consultation Duration (minutes)</Label>
                            <Input id="consultationDuration" type="number" placeholder="e.g., 30" value={profile.consultationDuration || ""} onChange={e => update("consultationDuration", parseInt(e.target.value) || 0)} />
                          </div>
                        )}
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div className="space-y-0.5">
                            <Label>Currently Accepting New Clients</Label>
                            <p className="text-xs text-muted-foreground">Make your profile visible to potential clients seeking advisors</p>
                          </div>
                          <Switch checked={profile.acceptingNewClients || false} onCheckedChange={checked => update("acceptingNewClients", checked)} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-muted-foreground" /><span>{profile.email}</span></div>
                  <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-muted-foreground" /><span>{profile.phoneNumber}</span></div>
                  <div className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-muted-foreground" /><span>{getFormattedAddress(profile.address) || ""}</span></div>
                  <div className="flex items-center gap-2 text-sm"><Calendar className="h-4 w-4 text-muted-foreground" /><span>Member since {memberSince}</span></div>
                  {profile.occupation?.occupationType && <div className="flex items-center gap-2 text-sm col-span-full"><span className="text-muted-foreground">Occupation:</span><span>{getFormattedOccupation(profile.occupation)}</span></div>}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Role-Specific Content */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-4">
          {r === "investor" && <InvestorOverview userId={userId} riskTolerance={profile.riskTolerance} portfolioSize={profile.portfolioSize} investmentExperience={profile.investmentExperience} investmentGoals={profile.investmentGoals} sectors={profile.sectors} />}
          {r === "analyst" && <AnalystOverview certifications={profile.certifications} focusSectors={profile.focusSectors} investmentFocus={profile.investmentFocus} researchExpertise={profile.researchExpertise} researchApproach={profile.researchApproach} yearsExperience={profile.yearsExperience} feeStructureOptions={profile.feeStructureOptions} clientRiskTolerance={profile.clientRiskTolerance} consultationAvailable={profile.consultationAvailable} consultationDuration={profile.consultationDuration} acceptingNewClients={profile.acceptingNewClients} avgClientReturn={analystAvgReturn ?? undefined} totalClients={analystTotalClients ?? undefined} />}
          {r === "advisor" && <AdvisorOverview advisorCertifications={profile.advisorCertifications} specializations={profile.specializations} yearsExperience={profile.yearsAdvising} assetsUnderManagement={advisorAum ?? undefined} totalClients={advisorTotalClients ?? undefined} feeStructureOptions={profile.feeStructureOptions} clientTypes={profile.clientTypes} clientRiskTolerance={profile.clientRiskTolerance} consultationAvailable={profile.consultationAvailable} consultationDuration={profile.consultationDuration} acceptingNewClients={profile.acceptingNewClients} />}
        </TabsContent>
        <TabsContent value="activity" className="space-y-4">
          {r === "investor" && <InvestorActivity userId={userId} />}
          {r === "analyst" && <AnalystActivity userId={userId} />}
          {r === "advisor" && <AdvisorActivity userId={userId} />}
        </TabsContent>
        <TabsContent value="settings" className="space-y-4">
          {r === "investor" && <InvestorSettings riskTolerance={profile.riskTolerance} portfolioSize={profile.portfolioSize} investmentExperience={profile.investmentExperience} investmentGoals={profile.investmentGoals} sectors={profile.sectors} />}
          {r === "analyst" && <AnalystSettings feeStructureOptions={profile.feeStructureOptions} consultationAvailable={profile.consultationAvailable} consultationDuration={profile.consultationDuration} acceptingNewClients={profile.acceptingNewClients} />}
          {r === "advisor" && <AdvisorSettings feeStructureOptions={profile.feeStructureOptions} specializations={profile.specializations} consultationAvailable={profile.consultationAvailable} consultationDuration={profile.consultationDuration} acceptingNewClients={profile.acceptingNewClients} />}
        </TabsContent>
        <TabsContent value="subscription" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
            </CardHeader>
            <CardContent>
              {subLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : !subStatus || subStatus.status === "NONE" || subStatus.status === "CANCELED" || subStatus.status === "EXPIRED" ? (
                <div className="space-y-4">
                  {subStatus && (subStatus.status === "CANCELED" || subStatus.status === "EXPIRED") && (
                    <div className="flex items-center justify-between p-4 bg-muted rounded-lg mb-4">
                      <div>
                        <p className="font-semibold">{subStatus.plan}{subStatus.plan === "ENTERPRISE" && subStatus.companySize ? ` (${subStatus.companySize} employees)` : ""}</p>
                        <p className="text-sm text-muted-foreground">{subStatus.status === "CANCELED" ? "Cancelled" : "Expired"}</p>
                      </div>
                      <p className="text-lg font-bold">
                        {subscriptionPrices.getPlanPrice(subStatus.plan, subStatus.companySize)}
                      </p>
                    </div>
                  )}
                  {(!subStatus || subStatus.status === "NONE") && (
                    <div className="flex items-center justify-between p-4 bg-muted rounded-lg mb-4">
                      <div>
                        <p className="font-semibold">{localStorage.getItem('plan') === 'ENTERPRISE' ? `Enterprise${localStorage.getItem('companySize') ? ` (${localStorage.getItem('companySize')} employees)` : ''}` : localStorage.getItem('userRole') === 'ANALYST' ? 'Analyst' : localStorage.getItem('userRole') === 'ADVISOR' ? 'Advisor' : 'Investor'}</p>
                        <p className="text-sm text-muted-foreground">{localStorage.getItem('plan') === 'ENTERPRISE' ? 'Enterprise plan' : localStorage.getItem('userRole') === 'INVESTOR' ? 'Free plan' : 'Subscribe to access all features'}</p>
                      </div>
                      <p className="text-lg font-bold">{localStorage.getItem('plan') === 'ENTERPRISE' ? subscriptionPrices.getPlanPrice(localStorage.getItem('plan') || '', localStorage.getItem('companySize')) : localStorage.getItem('userRole') === 'INVESTOR' ? 'Free' : subscriptionPrices.getPlanPrice(localStorage.getItem('plan') || '', localStorage.getItem('companySize'))}</p>
                    </div>
                  )}
                  {(localStorage.getItem('plan') === 'ENTERPRISE' ? localStorage.getItem('isOrgOwner') === 'true' : localStorage.getItem('userRole') !== 'INVESTOR') && (
                    <Button onClick={handleSubscribe}>
                      <CreditCard className="h-4 w-4 mr-2" />
                      {subStatus?.status === "CANCELED" || subStatus?.status === "EXPIRED" ? "Renew Subscription" : "Subscribe"}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <p className="font-semibold">{subStatus.plan}</p>
                      <p className="text-sm text-muted-foreground">
                        {subStatus.status === "ACTIVE" ? "Active" : subStatus.status === "TRIALING" ? "Trial" : subStatus.status}
                        {subStatus.plan === "ENTERPRISE" && subStatus.companySize ? ` (${subStatus.companySize} employees)` : ""}
                      </p>
                    </div>
                    <p className="text-lg font-bold">
                      {subscriptionPrices.getPlanPrice(subStatus.plan, subStatus.companySize)}
                    </p>
                  </div>
                  {(localStorage.getItem('plan') === 'ENTERPRISE' ? localStorage.getItem('isOrgOwner') === 'true' : localStorage.getItem('userRole') !== 'INVESTOR') && (
                  <div className="space-y-3">
                    {subStatus.status === "TRIALING" ? (
                      <Button onClick={handleSubscribe}>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Subscribe
                      </Button>
                    ) : !showCancelInput ? (
                      <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowCancelInput(true)}>
                        Cancel Subscription
                      </Button>
                    ) : (
                      <div className="space-y-3 p-4 border rounded-lg">
                        <textarea
                          className="w-full text-sm border rounded-md p-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                          rows={2}
                          placeholder="Optional reason for cancellation..."
                          value={cancelReason}
                          onChange={e => setCancelReason(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button variant="destructive" size="sm" onClick={handleCancelSubscription}>
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Confirm Cancel
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setShowCancelInput(false); setCancelReason(""); }}>
                            Back
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Profile Picture Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Upload Profile Picture</DialogTitle>
            <DialogDescription>Upload a new profile picture for your account. Maximum file size: 5MB</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!tempImage ? (
              <div className="flex items-center justify-center w-full">
                <label htmlFor="profile-picture" className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="h-10 w-10 text-muted-foreground mb-3" />
                    <p className="mb-2 text-sm text-muted-foreground"><span>Click to upload</span> or drag and drop</p>
                    <p className="text-xs text-muted-foreground">PNG, JPG, GIF up to 5MB</p>
                  </div>
                  <input id="profile-picture" type="file" className="hidden" accept="image/*" ref={fileInputRef} onChange={handleFileSelect} />
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-center w-full">
                  <div className="relative w-full h-48">
                    <img src={tempImage} alt="Preview" className="w-full h-full object-contain rounded-lg" />
                    <Button variant="destructive" size="sm" className="absolute top-2 right-2" onClick={() => { setTempImage(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleCancelUpload}>Cancel</Button>
            <Button onClick={handleSaveProfilePicture} disabled={!tempImage}><Camera className="h-4 w-4 mr-2" /> Save Picture</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
